import React from "react";
import { Fragment, ReactElement } from "react";
import { partial, partialRight, uniqBy } from "lodash";
import { css } from "@emotion/core";

import {
  ApiKeys,
  PaymentSource,
  PaymentSourceInput,
  PaymentSourceType,
  SupportedPaymentMethod,
} from "@zapier/payment/esm/types";
import {
  Alert,
  Modal,
  ModalContent,
  ModalContentBody,
  Text,
  Typography,
  useTheme,
} from "@zapier/design-system";
import { Currency } from "@zapier/currency";
import { CurrencyRulesetItem } from "@zapier/payment/esm/hooks/useCurrencyRuleset";
import { useSize } from "@zapier/payment/esm/hooks/useSize";
import { breakpoints } from "@zapier/payment/esm/constants";

import NoPaymentInformation from "../NoPaymentInformation";
import PaymentMethodBank from "../PaymentMethodBank";
import PaymentMethodCard from "../PaymentMethodCard";
import PaymentMethodPaypal from "../PaymentMethodPaypal";
import { resolveSupportedMethods } from "./utils";

export type PaymentMethodsSectionProps = {
  /** Temporary */
  accountId: number | undefined;
  /** API keys for payment processors */
  apiKeys: ApiKeys;
  /** The ruleset of which currencies can be paid using which payment sources */
  currencyRuleset: Array<CurrencyRulesetItem>;
  /** Current payment method */
  currentMethod: PaymentSourceType | null;
  /** Edit payment methods in a modal */
  editInModal: boolean;
  /** Error string  */
  error: string | undefined | null;
  /** Force user to update card details */
  forceCardUpdate: boolean;
  /** User has a saved payment source */
  hasPaymentSource: boolean;
  /** User is currently editing a payment method */
  isEditing: boolean;
  /** Current payment method editor is loading */
  isEditingInFlight: boolean;
  /** Call when users cancels edit */
  onCancelEdit: () => void;
  /** Call when user edits a value */
  onEdit: (a: PaymentSourceType | null) => void;
  /** Call when edit causes an error */
  onErrorEdit: (error: Error, method: PaymentSourceType | null) => void;
  /** Call to hide change success message */
  onHideSuccessMessage: () => void;
  /** Call when to save edit */
  onSaveEdit: (b: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType, hasSwitched: boolean) => void;
  /** Current saved payment method */
  savedSource: PaymentSource | undefined | null;
  /** The currently selected currency */
  selectedCurrency: Currency;
  /** Show payment changed successfully message */
  showChangeSuccessMessage: boolean;
  /** User's supported payment methods */
  supportedMethods: Array<SupportedPaymentMethod>;
};

const modalContentsCss = css`
  display: grid;
  grid-gap: 20px;
`;

const responsiveSizeCss = css`
  // I landed on 690px here because that is the size of the largest dialog of our payment forms (bank account form)
  // This makes all of the payment forms uniform and consistent in size at the large breakpoints
  width: 690px;
`;

const rebrandResponsiveSizeCss = css`
  width: 630px;
`;

const modalTitle = css`
  ${Typography.sectionHeader3}
  padding: 0;
  @media screen and (max-width: ${breakpoints.mobileMax}px) {
    ${Typography.paragraph1ShortBold}
    padding-top: 1.5rem;
  }
`;

const renderPaymentMethod = (
  props: PaymentMethodsSectionProps,
  filteredMethods: ReturnType<typeof resolveSupportedMethods>
): ReactElement => {
  const uniqueMethods = uniqBy(filteredMethods, m => m.method);
  const commonProps = {
    accountId: props.accountId,
    apiKeys: props.apiKeys,
    currentMethod: props.currentMethod,
    // Hide the switch if there's only one possible payment method
    hideSwitch:
      uniqueMethods.length <= 1 &&
      uniqueMethods.every(
        m =>
          m.method === props.savedSource?.method ||
          m.method === props.currentMethod
      ),
    isEditing: !props.editInModal && props.isEditing,
    onCancelEdit: props.onCancelEdit,
    onEdit: partial(props.onEdit, props.currentMethod),
    onErrorEdit: partialRight(props.onErrorEdit, props.currentMethod),
    onSaveEdit: props.onSaveEdit,
    onSelectSource: partialRight(props.onSelectSource, true),
    supportedMethods: filteredMethods,
    switchMethodOptions: uniqueMethods,
    selectedCurrency: props.selectedCurrency,
  };

  switch (props.currentMethod) {
    case PaymentSourceType.card: {
      return (
        <PaymentMethodCard
          {...commonProps}
          error={props.error}
          forceCardUpdate={props.forceCardUpdate}
          isEditingInFlight={props.isEditingInFlight}
          savedSource={
            props.savedSource?.kind === PaymentSourceType.card
              ? props.savedSource
              : null
          }
        />
      );
    }

    case PaymentSourceType.paypal:
      return (
        <PaymentMethodPaypal
          {...commonProps}
          savedSource={
            props.savedSource?.kind === PaymentSourceType.paypal
              ? props.savedSource
              : null
          }
        />
      );

    case PaymentSourceType.bank:
      return (
        <PaymentMethodBank
          {...commonProps}
          error={props.error}
          isEditingInFlight={props.isEditingInFlight}
          savedSource={
            props.savedSource?.kind === PaymentSourceType.bank
              ? props.savedSource
              : null
          }
        />
      );

    default:
      return (
        <NoPaymentInformation
          currentMethod={props.currentMethod}
          onSelectSource={props.onSelectSource}
          selectedCurrency={props.selectedCurrency}
          supportedMethods={filteredMethods}
        />
      );
  }
};

const renderPaymentSection = (props: PaymentMethodsSectionProps) => {
  const filteredMethods = resolveSupportedMethods({
    currencyRuleset: props.currencyRuleset,
    selectedCurrency: props.selectedCurrency,
    supportedMethods: props.supportedMethods,
  });

  const supportedPaymentMethods = filteredMethods.map(m => m.method);
  const supportedPaymentSubmethods = filteredMethods.map(m => m.submethod);

  const changeSuccessMessage = (
    <Alert onDismiss={props.onHideSuccessMessage} type="success">
      Changes have been saved
    </Alert>
  );

  const { method, submethod } = props.savedSource ?? {};

  const hasUnsupportedMethod = Boolean(
    method &&
      submethod &&
      !(
        supportedPaymentMethods.includes(method) &&
        supportedPaymentSubmethods.includes(submethod)
      )
  );

  /**
   * If a user has an unsupported payment method, let's log it to check for any false positives.
   */
  if (props.savedSource && hasUnsupportedMethod) {
    // eslint-disable-next-line no-console
    console.log(
      JSON.stringify({
        message: "Payment app: user has unsupported payment method",
        paymentSourceId: props.savedSource.id,
        paymentSourceMethod: props.savedSource.method,
        paymentSourceSubmethod: props.savedSource.submethod,
        supportedPaymentMethods,
        supportedPaymentSubmethods,
        selectedCurrency: props.selectedCurrency,
      })
    );
  }

  const unsupportedMethodMessage = (
    <Alert title="Heads up!" type="warning">
      Your payment method doesn't support{" "}
      <strong>
        {props.selectedCurrency.name} {props.selectedCurrency.symbol}
      </strong>
      . Please add a different method before checking out.
    </Alert>
  );

  return (
    <Fragment>
      {props.showChangeSuccessMessage && changeSuccessMessage}
      {hasUnsupportedMethod && !props.isEditing && unsupportedMethodMessage}
      {renderPaymentMethod(props, filteredMethods)}
    </Fragment>
  );
};

export const PaymentMethodsSection: React.FC<
  PaymentMethodsSectionProps
> = props => {
  const { isRebrand } = useTheme();
  const size = useSize();
  return (
    <Fragment>
      {renderPaymentSection(props)}
      {props.isEditing && props.editInModal && (
        <Modal ariaLabel="payment method" onClosed={props.onCancelEdit}>
          <ModalContent>
            <ModalContentBody scrollable={true}>
              <div
                css={[
                  modalContentsCss,
                  size === "large" && responsiveSizeCss,
                  size === "large" && isRebrand && rebrandResponsiveSizeCss,
                ]}
              >
                {isRebrand ? (
                  <h3 css={modalTitle}>Edit payment details</h3>
                ) : (
                  <Text type="sectionHeader3">Edit payment details</Text>
                )}
                {renderPaymentSection({ ...props, editInModal: false })}
              </div>
            </ModalContentBody>
          </ModalContent>
        </Modal>
      )}
    </Fragment>
  );
};
